import { useEffect, useState } from "react";
import { Plus, X, Building2, Mail, Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const inputCls = "mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs";
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

function AddCompanyDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ name: "", company_type: "gc", city: "", state: "", phone: "", website: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try {
      const c = await estApi.createCompany(form);
      toast.success(`${c.name} added`);
      onCreated(c);
      onOpenChange(false);
      setForm({ name: "", company_type: "gc", city: "", state: "", phone: "", website: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add company");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-company-dialog">
        <DialogHeader>
          <DialogTitle>Add Company</DialogTitle>
          <DialogDescription>
            One shared company list — the Bid Board reads these same records.
          </DialogDescription>
        </DialogHeader>
        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Name</span>
          <input value={form.name} onChange={(e) => set("name", e.target.value)}
                 className={inputCls} data-testid="company-name-input" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Type</span>
            <select value={form.company_type} onChange={(e) => set("company_type", e.target.value)}
                    className={inputCls} data-testid="company-type-input">
              {[["gc", "General Contractor"], ["architect", "Architect"], ["owner", "Owner"],
                ["manufacturer", "Manufacturer"], ["supplier", "Supplier"],
                ["subcontractor", "Subcontractor"]].map(([t, label]) => (
                <option key={t} value={t}>{label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Phone</span>
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                   className={inputCls} data-testid="company-phone-input" />
          </label>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="block col-span-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">City</span>
            <input value={form.city} onChange={(e) => set("city", e.target.value)}
                   className={inputCls} data-testid="company-city-input" />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">State</span>
            <select value={form.state} onChange={(e) => set("state", e.target.value)}
                    className={inputCls} data-testid="company-state-input">
              <option value="">—</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
                  data-testid="company-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={save} disabled={!form.name.trim()}
                  data-testid="company-save-btn">Add company</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Customers on this bid. Type to search the whole company directory — the list is 800+ records,
 *  so it is searched server-side, never a dropdown you scroll. No primary: every customer here is
 *  a real customer, each with its own contact and its own proposal email. */
export default function CompanyPicker({ value = [], companies, onChange, onCompanyAdded,
                                        onEmail }) {
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [found, setFound] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  const selected = value.map((id) => companies.find((c) => c.id === id)).filter(Boolean);

  // The directory is searched as you type; two letters is enough to be useful.
  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) { setFound([]); return undefined; }
    setSearching(true);
    const t = setTimeout(() => {
      estApi.getCompanies({ search: term, limit: 25 })
        .then((r) => setFound(r.companies || []))
        .catch(() => setFound([]))
        .finally(() => setSearching(false));
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  const pick = (c) => {
    if (!value.includes(c.id)) {
      onCompanyAdded?.(c);
      onChange([...value, c.id]);
    }
    setSearch("");
    setFound([]);
    setOpen(false);
  };

  return (
    <div data-testid="company-picker">
      <div className="mb-2 space-y-1.5">
        {selected.length === 0 && <span className="text-xs text-slate-400">No customer yet</span>}
        {selected.map((c) => (
          <div key={c.id}
               className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-700"
               data-testid={`selected-company-${c.id}`}>
            <Building2 className="h-3.5 w-3.5 text-slate-400" />
            <span className="font-medium">{c.name}</span>
            {c.city && <span className="text-[11px] text-slate-400">{c.city}, {c.state}</span>}
            {onEmail && (
              <button onClick={() => onEmail(c)} title={`Email the proposal to ${c.name}`}
                      data-testid={`email-company-${c.id}`}
                      className="ml-auto rounded-full p-1 text-slate-400 transition-colors hover:bg-white hover:text-[#007BFF]">
                <Mail className="h-3.5 w-3.5" />
              </button>
            )}
            <button onClick={() => onChange(value.filter((id) => id !== c.id))}
                    className={`rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-red-600 ${
                      onEmail ? "" : "ml-auto"}`}
                    data-testid={`remove-company-${c.id}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
                 onFocus={() => setOpen(true)}
                 placeholder="Type a customer name to search the directory…"
                 className="h-8 w-full rounded-md border border-slate-200 pl-7 pr-2 text-xs"
                 data-testid="company-search" />
          {open && search.trim().length >= 2 && (
            <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
                 data-testid="company-search-results">
              {searching && <p className="px-2 py-2 text-[11px] text-slate-400">Searching…</p>}
              {!searching && found.length === 0 && (
                <p className="px-2 py-2 text-[11px] text-slate-400">
                  No company matches "{search.trim()}" — add it instead.
                </p>
              )}
              {found.map((c) => (
                <button key={c.id} onClick={() => pick(c)}
                        disabled={value.includes(c.id)}
                        data-testid={`company-option-${c.id}`}
                        className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                  <span className="font-medium">{c.name}</span>
                  {c.city && (
                    <span className="text-[11px] text-slate-400">{c.city}, {c.state}</span>
                  )}
                  {value.includes(c.id) && (
                    <span className="ml-auto text-[10px] text-slate-400">already on the bid</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="add-gc-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add customer
        </Button>
      </div>
      {selected.length > 1 && (
        <p className="mt-1 text-[10px] text-slate-400">
          {selected.length} customers on this bid — the Proposals grid shows "Multiple GC
          ({selected.length})", and each one gets its own proposal email addressed to its own
          contact.
        </p>
      )}
      <AddCompanyDialog open={adding} onOpenChange={setAdding}
                        onCreated={(c) => { onCompanyAdded(c); onChange([...value, c.id]); }} />
    </div>
  );
}
